import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  FormControl,
  FormLabel,
  HStack,
  Heading,
  Input,
  Select,
  Spinner,
  Textarea,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { useAuth } from '../../context/AuthContext';

const SPAIN_IATA_CODES = [
  'ABC', 'ACE', 'AGP', 'ALC', 'BJZ', 'BCN', 'BIO', 'CDT', 'EAS', 'FUE',
  'GRO', 'GRX', 'GMZ', 'HSK', 'IBZ', 'ILD', 'JCU', 'LCG', 'LEI', 'LEN',
  'LPA', 'MAD', 'MAH', 'MLN', 'MJV', 'ODB', 'OVD', 'PNA', 'PMI', 'REU',
  'RJL', 'RMU', 'RGS', 'SCQ', 'SDR', 'SLM', 'SPC', 'SVQ', 'TFN', 'TFS',
  'TOJ', 'VDE', 'VGO', 'VIT', 'VLC', 'VLL', 'XRY', 'ZAZ',
];

const AdminVuelos = () => {
  const { accessToken: token } = useAuth();
  const toast = useToast();
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
  const isProductionApi = useMemo(() => apiBaseUrl.includes('overtaxi.es'), [apiBaseUrl]);

  const [isLoading, setIsLoading] = useState(true);
  const [providers, setProviders] = useState({
    flightapi: { enabled: false, configured: false, reachable: null, detail: '', status: 'disabled' },
    aeroapi: { enabled: false, configured: false, reachable: null, detail: '', status: 'disabled' },
  });
  const [pricingPayload, setPricingPayload] = useState({
    trip_type: 'Oneway',
    origin_iata: 'PMI',
    destination_iata: 'MAD',
    departure_date: '2026-02-27',
    adults: 1,
    children: 0,
    infants: 0,
    cabin: 'Economy',
    currency: 'EUR',
  });
  const [schedulePayload, setSchedulePayload] = useState({
    mode: 'dep',
    iata: 'PMI',
    year: 2026,
    month: 2,
    day: 27,
    page: 1,
  });
  const [trackingPayload, setTrackingPayload] = useState({
    num: 'VY2345',
    name: 'Vueling',
    date: '20260227',
  });
  const [testLoading, setTestLoading] = useState({ pricing: false, schedule: false, tracking: false });
  const [testResults, setTestResults] = useState({ pricing: null, schedule: null, tracking: null });

  const loadStatus = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${apiBaseUrl}/api/admin/vuelos/status`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Error HTTP ${response.status}`);
      }

      const data = await response.json();
      setProviders(data.providers || {});
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo cargar el estado de conexión de vuelos.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  }, [apiBaseUrl, toast, token]);

  useEffect(() => {
    if (token) {
      loadStatus();
    }
  }, [token, loadStatus]);

  const statusBadge = (status) => {
    if (status === 'connected') return <Badge colorScheme="green">Conectado</Badge>;
    if (status === 'error') return <Badge colorScheme="red">Error conexión</Badge>;
    if (status === 'not_configured') return <Badge colorScheme="orange">Sin configurar</Badge>;
    return <Badge colorScheme="gray">Deshabilitado</Badge>;
  };

  const providerCard = (title, providerData = { enabled: false, configured: false, reachable: null, detail: '', status: 'disabled' }) => (
    <Card variant="outline" borderColor="gray.300" w="100%">
      <CardBody>
        <VStack align="stretch" spacing={2}>
          <HStack justify="space-between">
            <Text fontWeight="bold">{title}</Text>
            {statusBadge(providerData.status)}
          </HStack>
          <Text fontSize="sm" color="gray.600">
            Habilitado en settings: {providerData.enabled ? 'Sí' : 'No'}
          </Text>
          <Text fontSize="sm" color="gray.600">
            Credenciales detectadas: {providerData.configured ? 'Sí' : 'No'}
          </Text>
          <Text fontSize="sm" color="gray.600">
            Estado técnico: {providerData.detail || 'Sin detalle'}
          </Text>
          {typeof providerData.queries_count === 'number' && (
            <Text fontSize="sm" color="gray.600">
              Queries ejecutadas: {providerData.queries_count}
            </Text>
          )}
        </VStack>
      </CardBody>
    </Card>
  );

  const runFlightApiTest = useCallback(async (kind, endpoint, payload) => {
    setTestLoading((prev) => ({ ...prev, [kind]: true }));
    try {
      const response = await fetch(`${apiBaseUrl}${endpoint}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      setTestResults((prev) => ({ ...prev, [kind]: data }));

      if (!response.ok) {
        throw new Error(`Error HTTP ${response.status}`);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: `No se pudo ejecutar el test de ${kind}.`,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setTestLoading((prev) => ({ ...prev, [kind]: false }));
    }
  }, [apiBaseUrl, token, toast]);

  const renderJsonResult = (result) => (
    <Textarea
      value={result ? JSON.stringify(result, null, 2) : ''}
      readOnly
      minH="200px"
      fontFamily="mono"
      fontSize="sm"
      placeholder="Resultado JSON del test..."
    />
  );

  return (
    <Box p={6}>
      <Card variant="outline" borderColor="gray.700">
        <CardHeader>
          <HStack justify="space-between">
            <Heading size="md">Vuelos (API {isProductionApi ? 'PROD' : 'DEV'})</Heading>
            <Button size="sm" onClick={loadStatus} isLoading={isLoading}>
              Refrescar
            </Button>
          </HStack>
        </CardHeader>
        <CardBody>
          {isLoading ? (
            <HStack>
              <Spinner size="sm" />
              <Text>Cargando estado de conexión...</Text>
            </HStack>
          ) : (
            <VStack spacing={4} align="stretch">
              {providerCard('FlightAPI', providers.flightapi)}
              {providerCard('AeroAPI', providers.aeroapi)}
            </VStack>
          )}
        </CardBody>
      </Card>

      <Card mt={6} variant="outline" borderColor="gray.700">
        <CardHeader>
          <Heading size="md">Tests FlightAPI</Heading>
        </CardHeader>
        <CardBody>
          <VStack spacing={4} align="stretch">
            <Card variant="outline" borderColor="gray.300">
              <CardHeader>
                <HStack justify="space-between">
                  <Heading size="sm">Pricing API</Heading>
                  <Button
                    size="sm"
                    colorScheme="teal"
                    isLoading={testLoading.pricing}
                    onClick={() => runFlightApiTest('pricing', '/api/admin/vuelos/flightapi/pricing/test', pricingPayload)}
                  >
                    Probar
                  </Button>
                </HStack>
              </CardHeader>
              <CardBody>
                <VStack spacing={3} align="stretch">
                  <HStack>
                    <FormControl>
                      <FormLabel>Trip type</FormLabel>
                      <Select value={pricingPayload.trip_type} onChange={(e) => setPricingPayload((p) => ({ ...p, trip_type: e.target.value }))}>
                        <option value="Oneway">Oneway</option>
                        <option value="Roundtrip">Roundtrip</option>
                      </Select>
                    </FormControl>
                    <FormControl>
                      <FormLabel>Origen IATA</FormLabel>
                      <Input
                        list="spain-iata-codes"
                        value={pricingPayload.origin_iata}
                        onChange={(e) => setPricingPayload((p) => ({ ...p, origin_iata: e.target.value.toUpperCase() }))}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Destino IATA</FormLabel>
                      <Input
                        list="spain-iata-codes"
                        value={pricingPayload.destination_iata}
                        onChange={(e) => setPricingPayload((p) => ({ ...p, destination_iata: e.target.value.toUpperCase() }))}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Fecha</FormLabel>
                      <Input type="date" value={pricingPayload.departure_date} onChange={(e) => setPricingPayload((p) => ({ ...p, departure_date: e.target.value }))} />
                    </FormControl>
                  </HStack>
                  <datalist id="spain-iata-codes">
                    {SPAIN_IATA_CODES.map((code) => (
                      <option key={code} value={code}>{code}</option>
                    ))}
                  </datalist>
                  <HStack>
                    <FormControl>
                      <FormLabel>Adultos</FormLabel>
                      <Input type="number" min={1} value={pricingPayload.adults} onChange={(e) => setPricingPayload((p) => ({ ...p, adults: Number(e.target.value) || 1 }))} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Niños</FormLabel>
                      <Input type="number" min={0} value={pricingPayload.children} onChange={(e) => setPricingPayload((p) => ({ ...p, children: Number(e.target.value) || 0 }))} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Bebés</FormLabel>
                      <Input type="number" min={0} value={pricingPayload.infants} onChange={(e) => setPricingPayload((p) => ({ ...p, infants: Number(e.target.value) || 0 }))} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Cabina</FormLabel>
                      <Select value={pricingPayload.cabin} onChange={(e) => setPricingPayload((p) => ({ ...p, cabin: e.target.value }))}>
                        <option value="Economy">Economy</option>
                        <option value="Business">Business</option>
                        <option value="First">First</option>
                      </Select>
                    </FormControl>
                    <FormControl>
                      <FormLabel>Moneda</FormLabel>
                      <Select value={pricingPayload.currency} onChange={(e) => setPricingPayload((p) => ({ ...p, currency: e.target.value }))}>
                        <option value="EUR">EUR</option>
                        <option value="USD">USD</option>
                      </Select>
                    </FormControl>
                  </HStack>
                  {renderJsonResult(testResults.pricing)}
                </VStack>
              </CardBody>
            </Card>

            <Card variant="outline" borderColor="gray.300">
              <CardHeader>
                <HStack justify="space-between">
                  <Heading size="sm">Airport Schedule API</Heading>
                  <Button
                    size="sm"
                    colorScheme="teal"
                    isLoading={testLoading.schedule}
                    onClick={() => runFlightApiTest('schedule', '/api/admin/vuelos/flightapi/schedule/test', schedulePayload)}
                  >
                    Probar
                  </Button>
                </HStack>
              </CardHeader>
              <CardBody>
                <VStack spacing={3} align="stretch">
                  <HStack>
                    <FormControl>
                      <FormLabel>Modo</FormLabel>
                      <Select value={schedulePayload.mode} onChange={(e) => setSchedulePayload((p) => ({ ...p, mode: e.target.value }))}>
                        <option value="dep">Salida (dep)</option>
                        <option value="arr">Llegada (arr)</option>
                      </Select>
                    </FormControl>
                    <FormControl>
                      <FormLabel>IATA aeropuerto</FormLabel>
                      <Input value={schedulePayload.iata} onChange={(e) => setSchedulePayload((p) => ({ ...p, iata: e.target.value.toUpperCase() }))} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Año</FormLabel>
                      <Input type="number" value={schedulePayload.year} onChange={(e) => setSchedulePayload((p) => ({ ...p, year: Number(e.target.value) || 2026 }))} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Mes</FormLabel>
                      <Select value={schedulePayload.month} onChange={(e) => setSchedulePayload((p) => ({ ...p, month: Number(e.target.value) || 1 }))}>
                        <option value={1}>Enero</option>
                        <option value={2}>Febrero</option>
                        <option value={3}>Marzo</option>
                        <option value={4}>Abril</option>
                        <option value={5}>Mayo</option>
                        <option value={6}>Junio</option>
                        <option value={7}>Julio</option>
                        <option value={8}>Agosto</option>
                        <option value={9}>Septiembre</option>
                        <option value={10}>Octubre</option>
                        <option value={11}>Noviembre</option>
                        <option value={12}>Diciembre</option>
                      </Select>
                    </FormControl>
                    <FormControl>
                      <FormLabel>Día</FormLabel>
                      <Input type="number" min={1} max={31} value={schedulePayload.day} onChange={(e) => setSchedulePayload((p) => ({ ...p, day: Number(e.target.value) || 1 }))} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Página</FormLabel>
                      <Input type="number" min={1} value={schedulePayload.page} onChange={(e) => setSchedulePayload((p) => ({ ...p, page: Number(e.target.value) || 1 }))} />
                    </FormControl>
                  </HStack>
                  {renderJsonResult(testResults.schedule)}
                </VStack>
              </CardBody>
            </Card>

            <Card variant="outline" borderColor="gray.300">
              <CardHeader>
                <HStack justify="space-between">
                  <Heading size="sm">Flight Tracking API</Heading>
                  <Button
                    size="sm"
                    colorScheme="teal"
                    isLoading={testLoading.tracking}
                    onClick={() => runFlightApiTest('tracking', '/api/admin/vuelos/flightapi/tracking/test', trackingPayload)}
                  >
                    Probar
                  </Button>
                </HStack>
              </CardHeader>
              <CardBody>
                <VStack spacing={3} align="stretch">
                  <HStack>
                    <FormControl>
                      <FormLabel>Número vuelo</FormLabel>
                      <Input value={trackingPayload.num} onChange={(e) => setTrackingPayload((p) => ({ ...p, num: e.target.value.toUpperCase() }))} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Aerolínea</FormLabel>
                      <Input value={trackingPayload.name} onChange={(e) => setTrackingPayload((p) => ({ ...p, name: e.target.value }))} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Fecha (YYYYMMDD)</FormLabel>
                      <Input value={trackingPayload.date} onChange={(e) => setTrackingPayload((p) => ({ ...p, date: e.target.value }))} />
                    </FormControl>
                  </HStack>
                  {renderJsonResult(testResults.tracking)}
                </VStack>
              </CardBody>
            </Card>
          </VStack>
        </CardBody>
      </Card>
    </Box>
  );
};

export default AdminVuelos;
